import {
  GenericFlyoutListMenuWidget, GenericMenuItemInfo, HorizontalOriginOption, MenuButtonWidget, VerticalOriginOption
} from '@tableau/widgets';
import * as React from 'react';

import { Messages } from '../../../../codegen/Localize';
import { ExplorableItemType } from '../../../../ts/models/ContentType';
import { ExploreContentApi_ContentType } from '../../../../ts/ServerApi/types/IContent';
import { ReactModalService } from '../../../../commonInfra/dialogs/ReactModalService';
import { UploadWorkbookDialogOpts } from '../../../../uploadContent/components/ConnectedUploadWorkbookDialog';
import { sendUploadWorkbookEntryPointTelemetry } from '../../../../uploadContent/utils/UploadWorkbookTelemetryEvents';
import { SpaceType } from '../../../state/SpaceInfo';
import { generateCreateContentMenuItem } from './CreateContentMenuItem';
import { sendCreateContentOpenMenuTelemetry, sendCreateContentSelectItemTelemetry } from './CreateContentTelemetry';
import { CreateContentType } from './CreateContentActionsList';
import { sendCreateCollectionClickedTelemetry, CreateCollectionClickedSourceOptions } from '../../Collections/CollectionsTelemetry';

export interface CreateContentButtonInfo {
  disabled: boolean;
  icon: string;
  title: string | JSX.Element;
  contentType: CreateContentType;
  isDivider: boolean;
}

export interface CreateContentButtonStateProps {
  createContentButtons: CreateContentButtonInfo[];
}

export interface CreateContentButtonDispatchProps {
  requestCreateContentButtonsInfo: () => void;
  createContent: (contentType: ExploreContentApi_ContentType | ExplorableItemType, projectId: string) => void;
}

export enum CreateContentButtonLocations {
  Home = 'Home',
  WelcomeChannel = 'WelcomeChannel',
  SiteSpace = 'SiteSpace',
  PersonalSpace = 'PersonalSpace'
}

export interface CreateContentButtonOwnProps {
  spaceType?: SpaceType;
  flyoutWidth?: number;
  projectId?: string;
  location?: CreateContentButtonLocations;
  useDarkTheme?: boolean;
  customButtonText?: string;
}

export type CreateContentButtonProps = CreateContentButtonStateProps & CreateContentButtonDispatchProps & CreateContentButtonOwnProps;

export interface CreateContentButtonState {
  showingCreateButtonsPanel: boolean;
}

export class CreateContentButton extends React.PureComponent<CreateContentButtonProps, CreateContentButtonState> {

  private createButtonRef: React.RefObject<HTMLButtonElement> = React.createRef();

  public constructor(props: CreateContentButtonProps) {
    super(props);
    this.state = { showingCreateButtonsPanel: false };
  }

  private showCreateButtonsPanel = (): void => {
    this.props.requestCreateContentButtonsInfo();
    this.setState({showingCreateButtonsPanel: !this.state.showingCreateButtonsPanel});
    sendCreateContentOpenMenuTelemetry(this.getTelemetryLocation());
  }

  private hideCreateButtons = (): void => {
    this.setState({showingCreateButtonsPanel: false});
  }

  private createButtonDisabled = (): boolean => {
    return this.props.createContentButtons.length === 0;
  }

  private shouldRenderCreateButtons = (): boolean => {
    return this.state.showingCreateButtonsPanel && !this.createButtonDisabled();
  }

  public render(): JSX.Element {
    return <div>
      <MenuButtonWidget
        aria-haspopup="menu"
        aria-controls={CreateButtonContentTestIds.CreateButtonsPanel}
        children={this.props.customButtonText ?? Messages.createContent_new()}
        onClick={this.showCreateButtonsPanel}
        disabled={this.createButtonDisabled()}
        kind={this.props.useDarkTheme ? 'outlineDark' : 'outline'}
        ref={this.createButtonRef}
        testId={CreateButtonContentTestIds.Button} />
      {this.shouldRenderCreateButtons() && this.renderCreateMenu()}
    </div>;
  }

  private renderCreateMenu = () => {
    return (
      <GenericFlyoutListMenuWidget
        allowAutoPosition
        anchorEl={this.createButtonRef.current}
        anchorOrigin={{
          vertical: VerticalOriginOption.bottom,
          horizontal: HorizontalOriginOption.left
        }}
        menuItemInfos={this.generateMenuItems()}
        onSelect={this.handleSelect}
        requestCloseAll={this.hideCreateButtons}
        id={CreateButtonContentTestIds.CreateButtonsPanel}
        testId={CreateButtonContentTestIds.CreateButtonsPanel}
        toggleEl={this.createButtonRef.current}
        menuStyle={{ minWidth: '150px', whiteSpace: 'nowrap' }}
      />
    );
  }

  private generateMenuItems = (): GenericMenuItemInfo[] => {
    const menuItems = this.props.createContentButtons.map(button => {
      if (button.isDivider) {
        return { isDivider: true };
      }

      return {
        displayFormatterBoundWithData: generateCreateContentMenuItem(button.title as string, button.icon, button.contentType, button.disabled),
        value: button.contentType as string,
        keyboardAccessibleValue: button.title,
        disabled: button.disabled
      };
    });

    return menuItems as GenericMenuItemInfo[];
  }

  private handleSelect = (value: string) => {
    this.hideCreateButtons();
    sendCreateContentSelectItemTelemetry(this.getTelemetryLocation(), value);
    if(value === 'collection') {
      sendCreateCollectionClickedTelemetry(this.props.spaceType, CreateCollectionClickedSourceOptions.NewMenu);
    }
    switch (value) {
      case 'workbookUpload': {
        ReactModalService.openCustom(UploadWorkbookDialogOpts(null, this.createButtonRef));
        sendUploadWorkbookEntryPointTelemetry('new-menu');
        break;
      }
      default: {
        const contentType = value as ExploreContentApi_ContentType | ExplorableItemType;
        this.props.createContent(contentType, this.props.projectId);
      }
    }
  }

  private getTelemetryLocation() {
    return this.props.location ?? this.props.spaceType;
  }
}

export namespace CreateButtonContentTestIds {
  export const Button = 'explorer-create-content';
  export const CreateButtonsPanel = 'create-content-buttons-panel';
}
